<script>
/*

  Required JSON structure for maintenance message:
    {
      "link": "http://essaygrader.ai/",
      "linkText": "👉 Check out",
      "text": "our Buyer’s Guide for K-12 schools and Higher Education Institutions",
      "buttonUrl": "/rubrics",
      "buttonLink": "", // not required
      "buttonText": "Learn More"
      "template": "primary" // "warning", "danger"
    }

  Required field:
    text or buttonURL

  */
import { mapGetters } from "vuex";

export default {
  inject: ["growthBook"],

  data() {
    return {
      maintenanceMessage: undefined,
      maintenance: undefined,
    };
  },
  computed: {
    ...mapGetters("config", ["features"]),
    show() {
      const isShown = !!(this.maintenanceMessage || this.maintenance);
      document.body?.classList[isShown ? "add" : "remove"]("has-maintenance");

      return isShown;
    },
    email() {
      return this.growthBook.attributes.email;
    },
  },

  watch: {
    email: {
      immediate: true,
      handler() {
        const maintenanceMessage = this.growthBook?.getFeatureValue(
          this.features.maintenanceMessageKey
        );
        if (!maintenanceMessage) {
          return;
        }

        if (typeof maintenanceMessage === "string") {
          return (this.maintenanceMessage = maintenanceMessage);
        }
        if (!maintenanceMessage.template) {
          maintenanceMessage.template = "primary";
        }
        if (maintenanceMessage?.text || maintenanceMessage.buttonURL) {
          this.maintenance = maintenanceMessage;
        }
      },
    },
  },
  methods: {
    handleClose() {
      this.maintenanceMessage = undefined;
      this.maintenance = undefined;
    },
  },
};
</script>

<template>
  <div
    v-if="show"
    class="maintenance-banner d-flex align-items-center justify-content-center"
    :class="`${'maintenance-banner-' + maintenance?.template}`"
  >
    <p
      v-if="maintenanceMessage"
      class="mb-0 text-center flex-grow-1"
      v-html="maintenanceMessage"
    />
    <div
      v-if="maintenance"
      class="paragraph-s d-flex align-items-center flex-gap"
    >
      <eg-button
        v-if="maintenance.link"
        type="ordinary"
        :href="maintenance.link"
        class="maintenance-link"
        target="_blank"
      >
        {{ maintenance.linkText }}
      </eg-button>
      <span v-if="maintenance.text" class="maintenance-text">{{
        maintenance.text
      }}</span>
      <eg-button
        v-if="maintenance.buttonUrl || maintenance.buttonLink"
        type="primary"
        size="small"
        color="white"
        :href="maintenance.buttonLink"
        :to="maintenance.buttonUrl"
        target="_blank"
        class="maintenance-button-right ml-2"
      >
        {{ maintenance.buttonText }}
      </eg-button>
    </div>
    <eg-button
      class="close-button"
      type="ordinary"
      icon="close"
      aria-label="Close"
      icon-color="white"
      @click="handleClose"
    />
  </div>
</template>
<style lang="scss" scoped>
.maintenance-banner {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  height: var(--maintenance-height);
  min-height: 44px;
  padding: 0.5rem 1.25rem;
  z-index: 111;
  color: white;
  background: var(--color-surface-primary-default);
  border-color: var(--color-surface-primary-default);
  border-radius: 0;
  display: flex;
  align-items: center;

  .close-button {
    position: absolute;
    right: 0.5rem;
    &:hover {
      background: transparent;
    }
  }
  .maintenance-button-right {
    color: var(--color-text-primary-default);
  }
  .maintenance-link:not(:disabled) {
    text-decoration-color: white;
    color: white;
    padding: 0;
    &:hover {
      background-color: transparent;
      text-decoration: underline;
    }
  }
  .maintenance-text {
    color: white;
    line-height: 18px;
  }
  &.maintenance-banner-warning {
    background: var(--color-surface-semantic-warning-light);
    .maintenance-link,
    .maintenance-text {
      color: var(--color-text-semantic-onWarning);
    }
    .maintenance-button-right {
      background: var(--color-surface-semantic-warning-default);
      color: white;
    }
  }
  &.maintenance-banner-danger {
    background: var(--color-surface-semantic-error-light);
    .maintenance-link,
    .maintenance-text {
      color: var(--color-text-semantic-onError);
    }
    .maintenance-button-right {
      background: var(--color-surface-semantic-error-error);
      color: white;
    }
  }
}
</style>
<style lang="scss">
body {
  --maintenance-height: 0px;
}

body.has-maintenance {
  --maintenance-height: 44px;
}
</style>
